import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTheme } from "@mui/material/styles";
import Button from "@components/Button/Button";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import { useNotification } from "@context/NotificationContext.tsx";
import type { FormContext } from "@features/port-security/components/SecurityClearence.types.ts";
import {
  emiratesIdNumberSchema,
  passportNumberSchema,
  type PortSecurityFormData,
} from "@features/port-security/schema/portSecurityFormSchema.ts";
import { usePortSecurityFormStore } from "@features/port-security/store/usePortSecurityForm.store.ts";
import { useLogout } from "@hooks/useLogout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import {
  checkPassportOrEmiratesId,
  getMySecurityClearance,
  getSecurityClearanceById,
} from "@services/securityClearanceApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import { runInquiry } from "@telemetry/runInquiry";
import { paths } from "@utils/navigation";
import {
  isIndividualApplicantUser,
  usesIndividualSecurityClearanceFlow,
} from "@utils/user-permissions";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useNavigate, useOutletContext } from "react-router-dom";
import { formatOnChangeEmiratesIDNumber, NO_PASSPORT_KEY } from "../utils";

const service = "bff-gatepass";
const LANG_BASE = "security-clearance";
const CHECK_ID_PASSPORT_ONLY_KEY = "checkIdPassportOnly";
const PASSPORT_CHECK_INQUIRY_CONTEXT = {
  module: "security_clearance",
  screen: "Security Clearance ID Check",
  action: "check_security_clearance_by_passport",
  entityType: "security_clearance",
};
const EMIRATES_ID_CHECK_INQUIRY_CONTEXT = {
  module: "security_clearance",
  screen: "Security Clearance ID Check",
  action: "check_security_clearance_by_emirates_id",
  entityType: "security_clearance",
};

interface IdCheckResponse {
  status?: string;
  expirationDate?: string;
  isPassportOrEmiratesIdNumberDuplicate: boolean;
  // Optional fields provided when name check is performed
  nameMatch?: boolean; // legacy BE key
  isNameMatching?: boolean; // current BE key
  securityClearanceId?: string;
}

const normalizeApplicantName = (value?: string | null) => {
  if (!value) return undefined;
  return value
    .trim()
    .split(/\s+/)
    .map((segment) => segment.toUpperCase())
    .join(" ");
};

const IdChecker: React.FC = () => {
  const theme = useTheme();
  const [id, setId] = useState<string>(usePortSecurityFormStore.getState().passportNumber || "");
  const [emiratesId, setEmiratesId] = useState<string>(
    usePortSecurityFormStore.getState().emiratesIdNumber || "",
  );
  const [status, setStatus] = useState<string | null>(null);
  const [expirationDate, setExpirationDate] = useState<string | null>(null);
  const [nameMismatch, setNameMismatch] = useState<boolean>(false);
  const [idExists, setIdExists] = useState<boolean>(false);
  const [passportOnlyModeFlag] = useState<boolean>(
    () => sessionStorage.getItem(CHECK_ID_PASSPORT_ONLY_KEY) === "1",
  );
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { updateFormData, resetPersistedForm, formData } = useOutletContext<FormContext>();
  const { logout } = useLogout();
  const user = useAuthStore((s) => s.user);
  const applicantName = useMemo(() => normalizeApplicantName(user?.name), [user?.name]);
  const isSecurityApplicant = user?.roles?.includes("security-applicant");
  const isIndividualUser = isIndividualApplicantUser(user?.roles);
  const usesIndividualFlow = usesIndividualSecurityClearanceFlow(user?.roles);
  const passportOnlyMode = passportOnlyModeFlag && usesIndividualFlow;
  const setSecurityClearanceRenewID = useAuthStore((s) => s.setSecurityClearanceRenewID);
  const setSecurityClearanceRequestStatus = useAuthStore(
    (s) => s.setSecurityClearanceRequestStatus,
  );
  const setSecurityClearanceRequestID = useAuthStore((s) => s.setSecurityClearanceRequestID);
  const setUser = useAuthStore((s) => s.setUser);

  const applySecurityClearanceToAuthStore = useCallback(
    async (scId: string, scStatus?: string | null) => {
      // Update both the SC identifiers and recompute permissions for gatepass availability
      setSecurityClearanceRequestID?.(scId);
      const normalizedStatus = scStatus ? String(scStatus).toUpperCase() : null;

      let finalStatus = normalizedStatus;
      if (!finalStatus) {
        try {
          const details = await getSecurityClearanceById({ id: scId, service });
          finalStatus = details?.status ? String(details.status).toUpperCase() : null;
        } catch {
          // If fetching status fails, set the id
        }
      }

      setSecurityClearanceRequestStatus?.(finalStatus ?? null);

      if (user) {
        // Recompute role-derived access permissions so gatepass becomes available.
        // updateBootstrap keeps the SC visible to hooks
        setUser(user, scId, finalStatus ?? null, { updateBootstrap: true });
      }
    },
    [setSecurityClearanceRequestID, setSecurityClearanceRequestStatus, setUser, user],
  );

  const mySecurityClearanceQuery = useQuery({
    queryKey: ["security-clearance", "me", user?.id],
    queryFn: () => getMySecurityClearance({ service }),
    enabled: isIndividualUser,
    retry: false,
  });

  useEffect(() => {
    const sc = mySecurityClearanceQuery.data as
      | {
          securityClearanceId?: string;
          securityClearanceRequestId?: string;
          id?: string;
          status?: string;
        }
      | undefined;
    const scId = String(
      sc?.securityClearanceId ?? sc?.securityClearanceRequestId ?? sc?.id ?? "",
    ).trim();
    if (!scId) {
      return;
    }
    applySecurityClearanceToAuthStore(scId, sc?.status ?? null);
    sessionStorage.removeItem(CHECK_ID_PASSPORT_ONLY_KEY);
    navigate(`/port-security/security-clearance-request-preview/${scId}`, { replace: true });
  }, [applySecurityClearanceToAuthStore, mySecurityClearanceQuery.data, navigate]);

  const checkId = useMutation({
    mutationFn: ({
      service,
      id,
      emiratesId,
    }: {
      service: string;
      id?: string;
      emiratesId?: string;
    }) => {
      const payload = {
        service,
        id,
        emiratesId,
        fullNameEnglish: isSecurityApplicant ? applicantName : undefined,
      };
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.log("[IdChecker] checkId payload", payload);
      }
      return runInquiry(
        PASSPORT_CHECK_INQUIRY_CONTEXT,
        () => checkPassportOrEmiratesId(payload) as Promise<IdCheckResponse>,
      );
    },
    onSuccess: async (data: IdCheckResponse) => {
      const {
        isPassportOrEmiratesIdNumberDuplicate,
        status,
        expirationDate,
        nameMatch,
        isNameMatching,
        securityClearanceId,
      } = data;
      const resolvedNameMatch = typeof isNameMatching === "boolean" ? isNameMatching : nameMatch;

      // If no passport/emirates ID in DB then do a new request form
      if (!isPassportOrEmiratesIdNumberDuplicate) {
        setIsPassportNumberSet?.(true);
        // Ensure passport number is in the store before navigation
        if (id) {
          updateFormData("passportNumber", id);
        }
        navigate(
          `/port-security/request-security-clearance/clearance-form/${paths.portSecurity.personalInfo}`,
          {
            state: { resetSCForm: true },
          },
        );
        return;
      }

      // If passport/emirates ID exists AND security clearance record exists then show Security Clearance details page
      // For `security-applicant` we rely on nameMatch; for `uae-pass-user` name match might be omitted by BE
      if (
        isPassportOrEmiratesIdNumberDuplicate &&
        securityClearanceId &&
        isIndividualUser &&
        (isSecurityApplicant ? resolvedNameMatch === true : resolvedNameMatch !== false)
      ) {
        await applySecurityClearanceToAuthStore(securityClearanceId, status);
        navigate(`/port-security/security-clearance-request-preview/${securityClearanceId}`);
        return;
      }

      // If passport/emirates ID exists BUT name doesn't match then show error message
      if (
        isPassportOrEmiratesIdNumberDuplicate &&
        resolvedNameMatch === false &&
        isSecurityApplicant
      ) {
        setNameMismatch(true);
        setStatus(null);
        return;
      }

      // For vendor/sponsor/security: If exists then show generic "already exist" message
      if (isPassportOrEmiratesIdNumberDuplicate && !isSecurityApplicant) {
        setIdExists(true);
        setStatus(null);
        setNameMismatch(false);
      } else if (isPassportOrEmiratesIdNumberDuplicate && status) {
        // Fallback for individuals when nameMatch is undefined and no securityClearanceId
        setStatus(status);
        if (expirationDate) {
          setExpirationDate(expirationDate);
        }
      }
    },
    onError: (error: Error) => {
      setStatus(null);
      notify({
        title: t(`${LANG_BASE}.notifications.check-id-error-title`),
        description: error?.message ?? t(`${LANG_BASE}.notifications.check-id-error-description`),
        type: "error",
      });
      console.error("Error checking ID:", error);
    },
  });

  const checkEmiratesId = useMutation({
    mutationFn: ({
      service,
      emiratesId,
      id,
    }: {
      service: string;
      emiratesId?: string;
      id?: string;
    }) => {
      const payload = {
        service,
        emiratesId,
        id,
        fullNameEnglish: isSecurityApplicant ? applicantName : undefined,
      };
      return runInquiry(
        EMIRATES_ID_CHECK_INQUIRY_CONTEXT,
        () => checkPassportOrEmiratesId(payload) as Promise<IdCheckResponse>,
      );
    },
    onSuccess: async (data: IdCheckResponse) => {
      const {
        isPassportOrEmiratesIdNumberDuplicate,
        status,
        expirationDate,
        nameMatch,
        isNameMatching,
        securityClearanceId,
      } = data;
      const resolvedNameMatch = typeof isNameMatching === "boolean" ? isNameMatching : nameMatch;

      // If no passport/emirates ID in DB then go to new request form
      if (!isPassportOrEmiratesIdNumberDuplicate) {
        setIsPassportNumberSet?.(false);
        // Ensure Emirates ID is in the store before navigation
        if (emiratesId) {
          updateFormData("emiratesIdNumber", emiratesId);
        }
        navigate(
          `/port-security/request-security-clearance/clearance-form/${paths.portSecurity.personalInfo}`,
          {
            state: { resetSCForm: true },
          },
        );
        return;
      }

      // If passport/emirates ID exists and security clearance record exists then show SC details page
      if (
        isPassportOrEmiratesIdNumberDuplicate &&
        securityClearanceId &&
        isIndividualUser &&
        (isSecurityApplicant ? resolvedNameMatch === true : resolvedNameMatch !== false)
      ) {
        await applySecurityClearanceToAuthStore(securityClearanceId, status);
        navigate(`/port-security/security-clearance-request-preview/${securityClearanceId}`);
        return;
      }

      // If passport/emirates ID exists but name doesn't match then show error message
      // (Only for individuals who sent name parameter)
      if (
        isPassportOrEmiratesIdNumberDuplicate &&
        resolvedNameMatch === false &&
        isSecurityApplicant
      ) {
        setNameMismatch(true);
        setStatus(null);
        return;
      }

      // For vendor/sponsor/security: If exists then show generic "already exist" message
      if (isPassportOrEmiratesIdNumberDuplicate && !isSecurityApplicant) {
        setIdExists(true);
        setStatus(null);
        setNameMismatch(false);
      } else if (isPassportOrEmiratesIdNumberDuplicate && status) {
        // Fallback for individuals when nameMatch is undefined and no securityClearanceId
        setStatus(status);
        if (expirationDate) {
          setExpirationDate(expirationDate);
        }
      }
    },
    onError: (error: Error) => {
      setStatus(null);
      notify({
        title: t(`${LANG_BASE}.notifications.check-id-error-title`),
        description: error?.message ?? t(`${LANG_BASE}.notifications.check-id-error-description`),
        type: "error",
      });
      console.error("Error checking ID:", error);
    },
  });

  const onFieldFocus = (fieldFocused: "emiratesIdNumber" | "passportNumber") => {
    const otherField = fieldFocused === "emiratesIdNumber" ? "passportNumber" : "emiratesIdNumber";
    if (otherField === "emiratesIdNumber") {
      setEmiratesId("");
    } else {
      setId("");
    }
    if (formData) {
      formData.setValue(otherField, "", { shouldDirty: true });
      if (formData.clearErrors) {
        formData.clearErrors(otherField);
      }
    }
    updateFormData(otherField, "");

    // reset check result message if focused field is empty
    const focusedValue = fieldFocused === "emiratesIdNumber" ? emiratesId : id;
    if (!focusedValue) {
      setStatus(null);
      setNameMismatch(false);
      setIdExists(false);
    }
  };

  const clearUploadStore = useUploadStore((state) => state.clearAll);

  useEffect(() => {
    resetPersistedForm?.();
    clearUploadStore();
    setSecurityClearanceRenewID?.(null);
    setSecurityClearanceRequestStatus?.(null);
    setId("");
    setEmiratesId("");
    updateFormData("emiratesIdNumber", "");
    updateFormData("passportNumber", "");
  }, [
    resetPersistedForm,
    clearUploadStore,
    setSecurityClearanceRenewID,
    setSecurityClearanceRequestStatus,
    updateFormData,
    passportOnlyMode,
  ]);

  const setIsPassportNumberSet = useAuthStore((s) => s.setIsPassportNumberSet);

  const handleClick = async (): Promise<void> => {
    if (!id && !emiratesId) {
      setStatus(null);
      setNameMismatch(false);
      return;
    }

    const schema = passportNumberSchema(10);
    const result = schema.safeParse(id || "");
    if (!result.success) {
      setStatus(null);
      setNameMismatch(false);
      return;
    }

    if (!passportOnlyMode && emiratesId) {
      const emiratesIdSchema = emiratesIdNumberSchema();
      const emiratesIdResult = emiratesIdSchema.safeParse(emiratesId);
      if (!emiratesIdResult.success) {
        setStatus(null);
        setNameMismatch(false);
        return;
      }
    }
    if (passportOnlyMode && !id) {
      setStatus(null);
      setNameMismatch(false);
      return;
    }
    setStatus(null);
    setNameMismatch(false);
    setIdExists(false);
    if (id !== "") {
      checkId.mutate({ service, id });
    }
    if (!passportOnlyMode && emiratesId !== "") {
      checkEmiratesId.mutate({ service, emiratesId });
    }
    localStorage.removeItem(NO_PASSPORT_KEY);
  };

  const renderMessage = () => {
    switch (status) {
      case "APPROVED":
        return (
          <div
            className="flex flex-row my-2 items-center"
            style={{ color: theme.palette.success.main }}
          >
            <CheckCircleOutlineIcon className="mr-1" />
            <p>{t(`${LANG_BASE}.passport-no-or-emirates-id-approved`, { date: expirationDate })}</p>
          </div>
        );
      case "PERMANENTLY_REJECTED":
      case "SUSPENDED":
        return (
          <div
            className="flex flex-row my-2 items-center"
            style={{ color: theme.palette.error.main }}
          >
            <HighlightOffIcon className="mr-1" />
            <p>{t(`${LANG_BASE}.passport-no-or-emirates-id-rejected`)}</p>
          </div>
        );
      case null:
        return null;
      default:
        // Covers PENDING and generic duplicates without name match
        return (
          <div
            className="flex flex-row my-2 items-center"
            style={{ color: theme.palette.warning.main }}
          >
            <WarningAmberIcon className="mr-1" />
            <p>{t(`${LANG_BASE}.passport-no-or-emirates-id-pending`)}</p>
          </div>
        );
    }
  };
  const hasCheckFailure = Boolean(status) || nameMismatch || idExists;
  const showBackToMainScreenButton = Boolean(user) && hasCheckFailure;

  return (
    <FormProvider {...formData}>
      <div className="flex flex-col items-center mt-20">
        <h1 className="text-2xl mb-6 text-center">{t(`${LANG_BASE}.check-sec-clearance`)}</h1>
        <div
          className="flex flex-col w-[400px] bg-white p-8 rounded-lg shadow-md"
          id="content"
          data-testid="id-checker-content"
        >
          <FormItem<PortSecurityFormData, "passportNumber">
            name="passportNumber"
            type="text"
            label={t(`${LANG_BASE}.passport-no`)}
            testId="security-passportNumber-input"
            value={id ?? ""}
            onFieldChange={(key, value) => {
              setId(value as string);
              if (value === "") {
                setStatus(null);
                setNameMismatch(false);
                setIdExists(false);
              }
              updateFormData(key, value);
            }}
            onFocus={() => onFieldFocus("passportNumber")}
            enforceUppercase
          />
          {id && (status || nameMismatch || idExists) && (
            <div id="messages-wrapper" className="mb-6">
              {nameMismatch ? (
                <div
                  className="flex flex-col my-2 items-start"
                  style={{ color: theme.palette.error.main }}
                >
                  <div className="flex flex-row items-center mb-2">
                    <WarningAmberIcon className="mr-1" />
                    <p>{t(`${LANG_BASE}.passport-emirates-id-name-mismatch`)}</p>
                  </div>
                </div>
              ) : idExists ? (
                <div
                  className="flex flex-col my-2 items-start"
                  style={{ color: theme.palette.success.main }}
                >
                  <div className="flex flex-row items-center mb-2">
                    <CheckCircleOutlineIcon className="mr-1" />
                    <p>{t(`${LANG_BASE}.passport-emirates-id-already-exist`)}</p>
                  </div>
                </div>
              ) : (
                renderMessage()
              )}
            </div>
          )}
          {!passportOnlyMode && (
            <>
              <FormItem<PortSecurityFormData, "emiratesIdNumber">
                name="emiratesIdNumber"
                type="text"
                label={t(`${LANG_BASE}.emirates-id`)}
                inputLabel={t(`${LANG_BASE}.emirates-id-label`)}
                testId="security-emiratesIdNumber-input"
                value={emiratesId ?? ""}
                onFieldChange={(key, value) => {
                  setEmiratesId(value as string);
                  if (value === "") {
                    setStatus(null);
                    setNameMismatch(false);
                    setIdExists(false);
                  }
                  updateFormData(key, value);
                }}
                onFocus={() => onFieldFocus("emiratesIdNumber")}
                formatOnChange={formatOnChangeEmiratesIDNumber}
              />
              {emiratesId && (status || nameMismatch || idExists) && (
                <div id="messages-wrapper" className="mb-6">
                  {nameMismatch ? (
                    <div
                      className="flex flex-col my-2 items-start"
                      style={{ color: theme.palette.error.main }}
                    >
                      <div className="flex flex-row items-center mb-2">
                        <WarningAmberIcon className="mr-1" />
                        <p>{t(`${LANG_BASE}.passport-emirates-id-name-mismatch`)}</p>
                      </div>
                    </div>
                  ) : idExists ? (
                    <div
                      className="flex flex-col my-2 items-start"
                      style={{ color: theme.palette.success.main }}
                    >
                      <div className="flex flex-row items-center mb-2">
                        <CheckCircleOutlineIcon className="mr-1" />
                        <p>{t(`${LANG_BASE}.passport-emirates-id-already-exist`)}</p>
                      </div>
                    </div>
                  ) : (
                    renderMessage()
                  )}
                </div>
              )}
            </>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={handleClick}
            disabled={id === "" && emiratesId === ""}
            testId="security-passportNumber-button"
            label={<span className="normal-case font-medium">{t(`${LANG_BASE}.check`)}</span>}
          />
          {showBackToMainScreenButton && (
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                navigate("/port-security");
              }}
              sx={{ mt: "28px" }}
              testId="back-to-main-screen-button"
              label={
                <span className="normal-case font-medium">
                  {t(`${LANG_BASE}.back-to-main-screen`)}
                </span>
              }
            />
          )}
          {!user && (
            <button
              className="mt-6 w-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-black font-medium rounded-none px-6 py-3 shadow-none border-0 outline-none focus:ring-2 focus:ring-blue-200 transition normal-case cursor-pointer"
              onClick={() => logout()}
              data-testid="back-to-login-button"
            >
              <span className="flex items-center justify-center w-full text-black normal-case">
                <ArrowBackIcon className="mr-2" fontSize="small" />
                {t(`${LANG_BASE}.back-to-log-in`)}
              </span>
            </button>
          )}
        </div>
      </div>
    </FormProvider>
  );
};
export default IdChecker;
